import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Group,
    Text,
    Image,
    Center,
    Flex,
    ActionIcon,
    RingProgress,
    Notification,
    Stack,
    Modal,
    Button,
    Box
} from '@mantine/core';
import { IconUpload, IconPhoto, IconCheck, IconX, IconCloud} from '@tabler/icons-react';
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { useAuth } from "../UseAuthHook";
import { useNavigate } from 'react-router-dom';
import {
    UploadService,
    UploadingFile,
    UploadStats,
    ConnectionStatus,
    UploadProgress
} from './UploadService';

export const FileUploader: React.FC = () => {
    const navigate = useNavigate();
    const { updateUserCounters, userProfile } = useAuth();
    const [files, setFiles] = useState<UploadingFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [isPickerActive, setIsPickerActive] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
        uploadProgress: 0,
        processingProgress: 0,
        duplicateDetectionProgress: 0,
        totalFiles: 0,
        uploadedFiles: 0,
        processedFiles: 0,
        duplicateDetectionComplete: false
    });
    const [uploadStats, setUploadStats] = useState<UploadStats>({
        uploadCount: userProfile?.uploadCount || 0,
        uploadLimit: userProfile?.uploadLimit || 0,
        remainingUploads: (userProfile?.uploadLimit || 0) - (userProfile?.uploadCount || 0)
    });
    
    // New state for notifications visibility
    const [showUploadNotification, setShowUploadNotification] = useState(false);
    const [showProcessingNotification, setShowProcessingNotification] = useState(false);
    const [showDuplicateNotification, setShowDuplicateNotification] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    // const [dropzoneWidth, setDropzoneWidth] = useState(0);
    const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
    const [duplicateStartTime, setDuplicateStartTime] = useState<number | null>(null);
    const [estimatedProcessingEndTime, setEstimatedProcessingEndTime] = useState<number | null>(null);
    const [estimatedDuplicateEndTime, setEstimatedDuplicateEndTime] = useState<number | null>(null);
    const [processingTimePerImage, setProcessingTimePerImage] = useState(4); // Initial estimate: 4s per image
    const [currentProgress, setCurrentProgress] = useState({ processing: 0, duplicate: 0 });
    // const [dropzoneWidth, setDropzoneWidth] = useState<number | null>(null);
    // const [dropzoneLeft, setDropzoneLeft] = useState<number>(0);
    // const dropzoneRef = useRef<HTMLDivElement>(null);
    // content reference (for sizing the bottom with the buttons)
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentWidth, setContentWidth] = useState<number | null>(null);

    // on mount, load events/unsorted & update container width
    // on unmount remove event window size observer
    useEffect(() => {
        if (contentRef.current) {
            setContentWidth(contentRef.current.offsetWidth);
        }

        const handleResize = () => {
            if (contentRef.current) {
                setContentWidth(contentRef.current.offsetWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Timers for auto-dismissal
    const uploadTimerRef = useRef<NodeJS.Timeout | null>(null);
    const processingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const duplicateTimerRef = useRef<NodeJS.Timeout | null>(null);

    const uploadServiceRef = useRef<UploadService | null>(null);

    // Initialize the UploadService
    // Update progress based on time
    useEffect(() => {
        const updateProgress = () => {
            const now = Date.now();

            // Update processing progress
            if (processingStartTime && estimatedProcessingEndTime && !uploadProgress.duplicateDetectionComplete) {
                const totalEstimatedTime = estimatedProcessingEndTime - processingStartTime;
                const elapsedTime = now - processingStartTime;
                const timeBasedProgress = Math.min(100, (elapsedTime / totalEstimatedTime) * 100);
                const fileBasedProgress = (uploadProgress.processedFiles / uploadProgress.uploadedFiles) * 100 || 0;

                // Update both currentProgress and uploadProgress for immediate UI update
                setCurrentProgress(prev => ({
                    ...prev,
                    processing: Math.max(timeBasedProgress, fileBasedProgress)
                }));

                // This is the key change - directly update the uploadProgress state
                setUploadProgress(prev => ({
                    ...prev,
                    processingProgress: Math.max(timeBasedProgress, fileBasedProgress, prev.processingProgress || 0)
                }));
            }

            // Similar update for duplicate detection...
            if (duplicateStartTime && estimatedDuplicateEndTime && !uploadProgress.duplicateDetectionComplete) {
                const totalDuplicateTime = estimatedDuplicateEndTime - duplicateStartTime;
                const elapsedTime = now - duplicateStartTime;
                const duplicateProgress = Math.min(100, (elapsedTime / totalDuplicateTime) * 100);

                setCurrentProgress(prev => ({
                    ...prev,
                    duplicate: duplicateProgress
                }));

                // Same here - update the state directly
                setUploadProgress(prev => ({
                    ...prev,
                    duplicateDetectionProgress: Math.max(duplicateProgress, prev.duplicateDetectionProgress || 0)
                }));
            }
        };

        const intervalId = setInterval(updateProgress, 100);
        return () => clearInterval(intervalId);
    }, [processingStartTime, estimatedProcessingEndTime, duplicateStartTime, estimatedDuplicateEndTime, uploadProgress]);

    useEffect(() => {
        const handleUpdates = (
            updatedFiles: UploadingFile[],
            progress: UploadProgress,
            status: ConnectionStatus
        ) => {
            setFiles(updatedFiles);
            setUploadProgress(progress);
            setConnectionStatus(status);

            // If we have files and they're being processed, set uploading to true
            if (updatedFiles.length > 0 && !progress.duplicateDetectionComplete) {
                setUploading(true);
            } else if (progress.duplicateDetectionComplete) {
                setUploading(false);
            }
            
            // Show relevant notifications based on progress
            if (progress.totalFiles > 0 && progress.uploadProgress < 100) {
                setShowUploadNotification(true);
            }

            // Start processing timer and set initial estimate
            if (progress.processedFiles === 0 && progress.uploadedFiles > 0 && !processingStartTime) {
                const now = Date.now();
                setProcessingStartTime(now);
                const baseTime = .9 * 60 * 1000; // 54 seconds in milliseconds // todo: this is very arbitrary and should at least be defined at the top for easier changes
                const initialEstimate = now + baseTime + (processingTimePerImage * 1000 * progress.totalFiles);
                setEstimatedProcessingEndTime(initialEstimate);
            }

            // Update processing progress based on time and adjust estimates
            if (processingStartTime && estimatedProcessingEndTime) {
                const now = Date.now();
                if (progress.processedFiles > 0) {
                    // Calculate actual time per image based on progress
                    const elapsedTime = now - processingStartTime;
                    const actualTimePerImage = elapsedTime / progress.processedFiles;
                    
                    // Adjust estimate if actual time differs significantly
                    if (Math.abs(actualTimePerImage - processingTimePerImage * 1000) > 1000) {
                        setProcessingTimePerImage(actualTimePerImage / 1000);
                        const remainingFiles = progress.uploadedFiles - progress.processedFiles;
                        const newEstimatedEnd = now + (actualTimePerImage * remainingFiles);
                        setEstimatedProcessingEndTime(newEstimatedEnd);
                    }
                }

                // Update the processing progress in the uploadProgress object
                // uploadProgress.processingProgress = Math.max(currentProgress.processing, uploadProgress.processingProgress || 0);

            }

            // Handle duplicate detection progress
            if (progress.processedFiles === progress.uploadedFiles && progress.uploadedFiles > 0) {
                if (!duplicateStartTime) {
                    const now = Date.now();
                    setDuplicateStartTime(now);
                    setEstimatedDuplicateEndTime(now + 50000); // 50 seconds
                }

                // if (duplicateStartTime && estimatedDuplicateEndTime) {
                //     uploadProgress.duplicateDetectionProgress = Math.max(currentProgress.duplicate, uploadProgress.duplicateDetectionProgress || 0);
                // }
            }

            // Show processing notification only after all files are uploaded
            if (progress.uploadedFiles === progress.totalFiles && progress.totalFiles > 0) {
                setShowProcessingNotification(true);
                
                // Keep upload notification visible for 3 seconds after completion
                if (progress.uploadProgress === 100) {
                    if (uploadTimerRef.current) {
                        clearTimeout(uploadTimerRef.current);
                    }
                    uploadTimerRef.current = setTimeout(() => {
                        setShowUploadNotification(false);
                        uploadTimerRef.current = null;
                    }, 3000);
                }
            }
            
            // Show duplicate detection notification only after all files are processed
            if (progress.processedFiles === progress.uploadedFiles && progress.uploadedFiles > 0) {
                setShowDuplicateNotification(true);
                
                // Keep processing notification visible for 3 seconds after completion
                if (progress.processingProgress === 100) {
                    if (processingTimerRef.current) {
                        clearTimeout(processingTimerRef.current);
                    }
                    processingTimerRef.current = setTimeout(() => {
                        setShowProcessingNotification(false);
                        processingTimerRef.current = null;
                    }, 3000);
                }
            }
            
            // Show completion modal when duplicate detection is complete
            if (progress.duplicateDetectionComplete) {
                // Keep duplicate notification visible for 3 seconds before showing modal
                if (duplicateTimerRef.current) {
                    clearTimeout(duplicateTimerRef.current);
                }
                duplicateTimerRef.current = setTimeout(() => {
                    setShowDuplicateNotification(false);
                    duplicateTimerRef.current = null;
                    setShowCompletionModal(true);
                }, 3000);
            }
        };

        const uploadService = new UploadService(handleUpdates);
        uploadServiceRef.current = uploadService;

        // Initialize the service
        uploadService.initialize().catch(error => {
            console.error('Failed to initialize upload service:', error);
        });

        // Clean up on unmount
        return () => {
            if (uploadServiceRef.current) {
                uploadServiceRef.current.cleanup();
            }
            
            // Clear any pending timers
            if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current);
            if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
            if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current);
        };
    }, []); // Removed showCompletionModal dependency

    // Update upload stats when userProfile changes
    useEffect(() => {
        if (userProfile) {
            setUploadStats({
                uploadCount: userProfile.uploadCount || 0,
                uploadLimit: userProfile.uploadLimit || 0,
                remainingUploads: (userProfile.uploadLimit || 0) - (userProfile.uploadCount || 0)
            });
        }
    }, [userProfile]);

    // Handle file drop
    const handleDrop = useCallback(async (acceptedFiles: FileWithPath[]) => {
        // If a file picker is already active, prevent another one from opening
        if (isPickerActive) {
            console.log('File picker is already active, ignoring request');
            return;
        }
        
        try {
            // Set picker active state to prevent multiple file picker requests
            setIsPickerActive(true);
            
            if (!uploadServiceRef.current || acceptedFiles.length === 0) return;

            // Log metadata for each file (temporary)
            acceptedFiles.forEach((file) => {
                console.log('File Metadata:', {
                  name: file.name,
                  path: (file as any).path || 'N/A', // Path is available only in Electron
                  lastModified: new Date(file.lastModified).toISOString(),
                  size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                  type: file.type
                });
            });

            // Check if we have enough uploads remaining
            if (uploadStats.remainingUploads <= 0) {
                console.error('Upload limit reached');
                return;
            }

            setUploading(true);
            // Reset notifications when starting a new upload
            setShowUploadNotification(true);
            setShowProcessingNotification(false);
            setShowDuplicateNotification(false);
            setShowCompletionModal(false);

            await uploadServiceRef.current.uploadFiles(acceptedFiles, uploadStats.remainingUploads);
            updateUserCounters({
                uploadCount: userProfile.uploadCount! + acceptedFiles.length,
            });
        } catch (error) {
            console.error('Error uploading files:', error);
            setUploading(false);
        } finally {
            // Set a timeout before allowing another file picker to be opened
            // This prevents the "File picker already active" error in Chrome
            setTimeout(() => {
                setIsPickerActive(false);
            }, 500);
        }
    }, [uploadStats.remainingUploads, userProfile, isPickerActive]);

    // Handle navigation to sorting tab
    const handleGoToSort = () => {
        setShowCompletionModal(false);
        navigate('/sort-images'); // Adjust this path to your sorting tab route
    };

    // Handle continue uploading option
    const handleContinueUploading = () => {
        setShowCompletionModal(false);
    };

    // todo: this could probably be a function in a helper somewhere? todo: this should be
    function getImageOrientation(file: File): Promise<number> {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event: ProgressEvent<FileReader>) => {
                if (!event.target?.result) {
                    resolve(-1);
                    return;
                }

                const view = new DataView(event.target.result as ArrayBuffer);
                if (view.getUint16(0, false) !== 0xFFD8) {
                    resolve(-1);
                    return;
                }

                let offset = 2;
                while (offset < view.byteLength) {
                    const marker = view.getUint16(offset, false);
                    offset += 2;

                    if (marker === 0xFFE1) {
                        if (view.getUint32(offset + 2, false) !== 0x45786966) {
                            resolve(-1);
                            return;
                        }

                        const littleEndian = view.getUint16(offset + 8, false) === 0x4949;
                        offset += view.getUint32(offset + 4, littleEndian);
                        const tags = view.getUint16(offset, littleEndian);
                        offset += 2;

                        for (let i = 0; i < tags; i++) {
                            const tagOffset = offset + i * 12;
                            if (view.getUint16(tagOffset, littleEndian) === 0x0112) {
                                resolve(view.getUint16(tagOffset + 8, littleEndian));
                                return;
                            }
                        }
                    } else if ((marker & 0xFF00) !== 0xFF00) {
                        break;
                    } else {
                        offset += view.getUint16(offset, false);
                    }
                }
                resolve(-1);
            };
            reader.readAsArrayBuffer(file);
        });
    }


    // Generate previews for files
    const previews = files.map(({ file, status, fileId, filePath }) => {
        const imageUrl = URL.createObjectURL(file);
        return (
            <div key={fileId} style={{
                position: 'relative',
                width: 'calc((100% - 12px) / 4)',
                aspectRatio: 1 / 1,
            }}>
                <Image src={imageUrl} style={{aspectRatio: 1 / 1}} onLoad={() => URL.revokeObjectURL(imageUrl)}/>
                {/*{filePath && (*/}
                {/*    <Text size="xs" truncate style={{position: 'absolute', bottom: '3px', left: '3px', right: '3px', background: 'rgba(0,0,0,0.5)', color: 'white', padding: '2px'}}>*/}
                {/*        {filePath}*/}
                {/*    </Text>*/}
                {/*)}*/}
                {status === 'uploaded' && (
                    <ActionIcon color="blue" variant="filled" radius="xl" size="sm"
                                style={{position: 'absolute', top: '3px', right: '3px'}}>
                        <IconCloud size="15px"/>
                    </ActionIcon>
                )}
                {status === 'processed' && (
                    <ActionIcon color="teal" variant="filled" radius="xl" size="sm"
                                style={{position: 'absolute', top: '3px', right: '3px'}}>
                        <IconCheck size="15px"/>
                    </ActionIcon>
                )}
                {status === 'failed' && (
                    <ActionIcon color="red" variant="filled" radius="xl" size="sm"
                                style={{position: 'absolute', top: '3px', right: '3px'}}>
                        <IconX size="15px"/>
                    </ActionIcon>
                )}
            </div>
        );
    });

    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
            <Center style={{width: '100%'}}>
                <Dropzone
                    ref={contentRef}
                    onDrop={handleDrop}
                    maxSize={15 * 1024 ** 2}
                    accept={[
                        ...IMAGE_MIME_TYPE,
                        'image/tiff'
                    ]}
                    style={{ width: '100%', backgroundColor: 'var(--mantine-color-gray-light)' }}
                    // loading={uploading || uploadStats.remainingUploads <= 0}
                    disabled={uploading || uploadStats.remainingUploads <= 0}
                >
                    <Group justify="center" gap="xl" mih={220} style={{pointerEvents: 'none', width: '100%'}}>
                        <Dropzone.Accept>
                            <IconUpload
                                style={{width: '52px', height: '52px', color: 'var(--mantine-color-blue-6)'}}
                                stroke={1.5}
                            />
                        </Dropzone.Accept>
                        <Dropzone.Idle>
                            <IconPhoto
                                style={{width: '52px', height: '52px', color: 'var(--mantine-color-dimmed)'}}
                                stroke={1.5}
                            />
                        </Dropzone.Idle>
                        <div>
                            <Text size="xl" inline>
                                Drag images here or click to select files
                            </Text>
                            <Text size="sm" c="dimmed" inline mt={7}>
                                Attach as many files as you like, each file should not exceed 15MB.
                            </Text>
                            <Text size="sm" c="dimmed" inline mt={7}>
                                You have {uploadStats.remainingUploads} uploads remaining.
                            </Text>
                            {/*{connectionStatus !== 'connected' && (*/}
                            {/*    <Text size="sm" c="red" inline mt={7}>*/}
                            {/*        {connectionStatus === 'connecting' ? 'Connecting...' : 'Not connected. Trying to reconnect...'}*/}
                            {/*    </Text>*/}
                            {/*)}*/}
                        </div>
                    </Group>
                </Dropzone>
            </Center>

            <Flex
                mt="md"
                direction="row"
                wrap="wrap"
                gap={{base: '3px', sm: '3px'}}
                justify={{sm: '3px'}}

            >
                {previews}
            </Flex>

            {/* Fixed position notifications container */}
            <Box
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    zIndex: 2,
                    width: contentRef.current ? `${contentRef.current.getBoundingClientRect().width}px` : '100%',
                    left: contentRef.current?.getBoundingClientRect().left || 0,
                }}
            >
                <Stack 
                    gap="sm"
                >
                    {/* Upload Progress Notification */}
                    {showUploadNotification && (
                        <Notification
                            icon={
                                uploadProgress.uploadProgress === 100 ? (
                                    <RingProgress
                                        ml={'sm'} mr={'sm'}
                                        sections={[{value: 100, color: 'blue'}]}
                                        label={
                                            <Center>
                                                <ActionIcon color="blue" variant="light" radius="xl" size="xl">
                                                    <IconCheck size="20px"/>
                                                </ActionIcon>
                                            </Center>
                                        }
                                        size={50}
                                        thickness={4}
                                    />
                                ) : (
                                    <RingProgress
                                        ml={'sm'} mr={'sm'}
                                        sections={[{value: uploadProgress.uploadProgress, color: 'blue'}]}
                                        label={
                                            <Text c="blue" fw={700} ta="center" size="sm">
                                                {Math.round(uploadProgress.uploadProgress)}%
                                            </Text>
                                        }
                                        size={50}
                                        thickness={4}
                                    />
                                )
                            }
                            withBorder
                            color={'none'}
                            radius="lg"
                            title="File Upload"
                            onClose={() => setShowUploadNotification(false)}
                        >
                            <Text>{uploadProgress.uploadedFiles}/{uploadProgress.totalFiles} files uploaded</Text>
                            <Text size="sm" c="dimmed">
                                Upload limit: {uploadStats.uploadCount}/{uploadStats.uploadLimit} ({uploadStats.remainingUploads} remaining)
                            </Text>
                        </Notification>
                    )}

                    {/* Processing Progress Notification */}
                    {showProcessingNotification && (
                        <Notification
                            icon={
                                uploadProgress.processingProgress === 100 ? (
                                    <RingProgress
                                        ml={'sm'} mr={'sm'}
                                        sections={[{value: 100, color: 'green'}]}
                                        label={
                                            <Center>
                                                <ActionIcon color="green" variant="light" radius="xl" size="xl">
                                                    <IconCheck size="20px"/>
                                                </ActionIcon>
                                            </Center>
                                        }
                                        size={50}
                                        thickness={4}
                                    />
                                ) : (
                                    <RingProgress
                                        ml={'sm'} mr={'sm'}
                                        sections={[{value: uploadProgress.processingProgress, color: 'green'}]}
                                        label={
                                            <Text c="green" fw={700} ta="center" size="sm">
                                                {Math.round(uploadProgress.processingProgress)}%
                                            </Text>
                                        }
                                        size={50}
                                        thickness={4}
                                    />
                                )
                            }
                            withBorder
                            color={'none'}
                            radius="lg"
                            title="Image Processing"
                            onClose={() => setShowProcessingNotification(false)}
                        >
                            <Text>{uploadProgress.processedFiles}/{uploadProgress.uploadedFiles} files processed</Text>

                        </Notification>
                    )}

                    {/* Duplicate Detection Notification */}
                    {showDuplicateNotification && (
                        <Notification
                            icon={
                                uploadProgress.duplicateDetectionComplete ? (
                                    <RingProgress
                                        ml={'sm'} mr={'sm'}
                                        sections={[{value: 100, color: 'violet'}]}
                                        label={
                                            <Center>
                                                <ActionIcon color="violet" variant="light" radius="xl" size="xl">
                                                    <IconCheck size="20px"/>
                                                </ActionIcon>
                                            </Center>
                                        }
                                        size={50}
                                        thickness={4}
                                    />
                                ) : (
                                    <RingProgress
                                        ml={'sm'} mr={'sm'}
                                        sections={[{value: uploadProgress.duplicateDetectionProgress, color: 'violet'}]}
                                        label={
                                            <Text c="violet" fw={700} ta="center" size="sm">
                                                {Math.round(uploadProgress.duplicateDetectionProgress)}%
                                            </Text>
                                        }
                                        size={50}
                                        thickness={4}
                                    />
                                )
                            }
                            withBorder
                            color={'none'}
                            radius="lg"
                            title={uploadProgress.duplicateDetectionComplete ? "Duplicate Detection Complete" : "Detecting Duplicates"}
                            onClose={() => setShowDuplicateNotification(false)}
                        >
                            <Text>
                                {uploadProgress.duplicateDetectionComplete
                                    ? "All duplicates have been detected"
                                    : "Analyzing images for duplicates..."}
                            </Text>
                        </Notification>
                    )}
                </Stack>
            </Box>
            
            {/* Completion Modal */}
            <Modal
                opened={showCompletionModal}
                onClose={handleContinueUploading}
                title="Images Uploaded Successfully!"
                centered
                radius="lg"
                size="md"
            >
                <Text size="md" mb="lg">
                    Your images have been successfully uploaded and processed. You can now start sorting them.
                </Text>
                <Group mt="xl">
                    {/* <Button variant="outline" onClick={handleContinueUploading}>
                        Continue Uploading
                    </Button> */}
                    <Button fullWidth color="blue" onClick={handleGoToSort}>
                        Start Sorting
                    </Button>
                </Group>
            </Modal>
        </div>
    );
};